import { RowSelectionProvider } from "@/components/data-table/context/RowSelectionProvider";
import PageHeader from "@/components/PageHeader";
import Spinner from "@/components/Spinner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmailTypeAllowedRolesTable } from "@/modules/email-types/components/EmailTypeAllowedRolesTable";
import { EmailTypeEditorForm } from "@/modules/email-types/components/EmailTypeEditorForm";
import { EmailTypeEditorRolesDialog } from "@/modules/email-types/components/EmailTypeEditorRolesDialog";
import { EmailTypeEditorSection } from "@/modules/email-types/components/EmailTypeEditorSection";
import { useEmailTypeBreadcrumbs } from "@/modules/email-types/hooks/useEmailTypeBreadcrumbs";
import { useEmailTypeForm } from "@/modules/email-types/hooks/useEmailTypeForm";
import { useEmailTypeId } from "@/modules/email-types/hooks/useEmailTypeId";
import { useEmailTypeQuery } from "@/modules/email-types/hooks/useEmailTypeQuery";
import { useUpdateEmailTypeMutation } from "@/modules/email-types/hooks/useUpdateEmailTypeMutation";
import { useUpdateEmailTypeRolesMutation } from "@/modules/email-types/hooks/useUpdateEmailTypeRolesMutation";
import { useState } from "react";
import { useTranslation } from "react-i18next";
export const EmailTypeEditorDirectory = () => {
  const { t } = useTranslation();
  const emailTypeId = useEmailTypeId();
  const [isRolesDialogOpen, setIsRolesDialogOpen] = useState(false);
  const { query, emailType } = useEmailTypeQuery(emailTypeId);
  const breadcrumbs = useEmailTypeBreadcrumbs(emailType?.name);
  const updateTypeMutation = useUpdateEmailTypeMutation(emailTypeId);
  const updateRolesMutation = useUpdateEmailTypeRolesMutation(emailTypeId);
  const { form, handleSubmit } = useEmailTypeForm({
    description: emailType?.description,
    onSubmitDescription: updateTypeMutation.mutateAsync,
  });

  if (query.isLoading || query.isPending) {
    return (
      <>
        <PageHeader breadcrumbItems={breadcrumbs} />
        <Spinner label={t("table.loading")} className="h-full w-full" />
      </>
    );
  }

  if (!emailType) {
    return null;
  }

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbs} />
      <div className="space-y-6 p-4">
        <Card>
          <CardHeader>
            <CardTitle>{t("emailTypes.editor.detailsSectionTitle")}</CardTitle>
          </CardHeader>
          <CardContent>
            <EmailTypeEditorSection emailType={emailType} />
            <EmailTypeEditorForm
              form={form}
              isSubmitting={updateTypeMutation.isPending}
              onSubmit={handleSubmit}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("emailTypes.roles.title")}</CardTitle>
          </CardHeader>
          <CardContent>
            <RowSelectionProvider>
              <EmailTypeAllowedRolesTable
                roles={emailType.allowed_roles}
                isSubmitting={updateRolesMutation.isPending}
                onOpenAddRoles={() => setIsRolesDialogOpen(true)}
                onRemoveRoles={(roleIds) =>
                  updateRolesMutation.mutate({ delete: roleIds })
                }
              />
            </RowSelectionProvider>
          </CardContent>
        </Card>
      </div>

      <EmailTypeEditorRolesDialog
        emailTypeId={emailTypeId}
        isOpen={isRolesDialogOpen}
        onOpenChange={setIsRolesDialogOpen}
      />
    </>
  );
};
